// DocumentLibrary — Documents page.
//
// Round-3.11 (COO ctsk-049 epic) work landed here:
//   ctsk-050  row ellipse menu restored (7 actions per spec §2.5)
//   ctsk-052  Share link row action (copy permalink, no ACL change)
//   ctsk-053  Tag quick-filter chip row (Untagged / In a nest / Recent /
//             Needs review / Starred tags — the last is grey-disabled
//             until starred-tag infra ships)
//   ctsk-054  Pagination + per-page selector + URL-write (?page=&limit=)
//
// Pagination model: mirrors WorkItemsList (#83). Server-side via the new
// offset/total branch on /documents (cursor mode untouched for SDK
// consumers like hyper-scanno). Page resets to 1 on any filter change so
// the user never ends up on a page past the new totalPages.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ExternalLink, Layers, Trash2, X } from 'lucide-react';
import { CopyShortUrlButton } from '../components/CopyShortUrlButton.js';
import { useSession } from '../hooks/useSession.js';
import { useMediaQuery } from '../hooks/useMediaQuery.js';
import { snippetSegments } from '../components/DocumentSearch/highlight.js';
import { CardViewBar } from '../components/documents/CardViewBar.js';
import { DocumentCardWall } from '../components/documents/DocumentCardWall.js';
import { effectiveViewMode, loadCols, loadHighlightEffect, loadPreviewAll, loadScaled, rememberOpened, takeLastOpened, loadViewMode, saveCols, savePreviewAll, saveScaled, saveViewMode, type ColsPref, type ViewMode } from '../components/documents/viewPrefs.js';
import { BulkUploadPanel, useBulkUpload } from '../components/BulkUploadQueue.js';
import { BulkEditLabelsModal, type BulkEditMutations } from '../components/BulkEditLabelsModal.js';
import { LabelFilterChip } from '../components/LabelFilterChip.js';
import { FolderDropDialog } from '../components/FolderDropDialog.js';
import {
  collectDroppedItems,
  filesFromPicker,
  readDroppedEntries,
  summarizeDrop,
  type CollectedDrop,
  type FolderDropSummary,
  type WalkResult,
} from '../lib/dropped-entries.js';
import { useDocumentGrouping } from '../hooks/useDocumentGrouping.js';
import { useHiddenNests } from '../hooks/useHiddenNests.js';
import { HiddenNestChips } from '../components/HiddenNestChips.js';
import type {
  DocumentBulkEditResponse,
  DocumentHealth,
  DocumentListItem,
  DocumentSearchResponse,
  DocumentStatus,
  IntakeClass,
  StorageContainer,
  Tag,
} from '@captain-tasko/shared';
import {
  DOCUMENT_HEALTH,
  DOCUMENT_SEARCH_LIMIT_MAX,
  DOCUMENT_SEARCH_QUERY_MAX,
  DOCUMENT_SEARCH_QUERY_MIN,
  DOCUMENT_STATUSES,
  INTAKE_CLASSES,
} from '@captain-tasko/shared';
import { ApiError, apiFetch, apiList, apiSingle } from '../lib/api.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { TagBadge } from '../components/TagBadge.js';
import { toast } from '../components/Toast.js';
import { useRealtime } from '../lib/realtime.js';
import { formatBytes, iconLabelForMime } from '../lib/format.js';
import { MimeIcon } from '../components/MimeIcon.js';
import { SortableTh } from '../components/SortableTh.js';
import { useTableSort } from '../lib/sort.js';
import { RowActionsMenu } from '../components/RowActions.js';
import { Pagination } from '../components/Pagination.js';
import { copyToClipboard } from '../lib/clipboard.js';
import { NestFilterChip } from '../components/NestFilterChip.js';
import { ColumnsMenu, useColPrefs, type ColumnDef } from '../components/ColumnsMenu.js';
// CTSK-494 Phase 1c — full-page search mode: when ?q= is present and the
// document-search flag is on, this page calls the FTS-backed
// GET /documents/search and renders highlight rows (design doc §3.8)
// instead of the title-ILIKE list. Same row body as the header dropdown.
import { FEATURES_QUERY_KEY, useFeatures } from '../lib/features.js';
import { DocumentHighlightRow, RelatedFindingsStrip, type LabelKind } from '../components/DocumentSearch/index.js';

interface Filters {
  q: string;
  tags: string[];                // tag IDs
  containerId: string;
  intakeClasses: IntakeClass[];
  statuses: DocumentStatus[];
  health: DocumentHealth[];
  dateFrom: string;
  dateTo: string;
}

// ctsk-061 — column config for the Documents table. The reusable
// hook/component lives in components/ColumnsMenu.tsx; this list is the
// Documents-specific column definitions plus a stable storage key.
//
// Widths chosen so the row breathes: Title gets a generous but capped
// span (300, with maxWidth + ellipsis in the cell), Tags gets enough
// space for ~3 chips before wrapping (280), and the time / size /
// container columns are sized to the content they show.
const DOC_COLUMNS: ColumnDef[] = [
  // CTSK-246 — watch/star column, mirroring CTSK-237's WorkItems
  // pattern. Index 0 of DOC_COLUMNS = leftmost data column, which
  // renders immediately AFTER the bulk-select checkbox (the checkbox
  // cell lives outside the column config) — "after the selection
  // checkbox" per the ticket's AC#1. Icon-only header, 32px tight.
  // Sortable — desc bubbles watched docs to the top; default sort
  // stays updatedAt desc (opt-in, same as CTSK-237 AC#3).
  { key: 'watch',        label: '',          width: 32,  defaultVisible: true,  sortable: true },
  { key: 'title',        label: 'Title',     width: 300, defaultVisible: true,  sortable: true },
  // HTML data type — "Open in browser" shortcut. Narrow, unsorted,
  // sits between Title and Intake/Status. Cells render empty for
  // non-HTML mime types so the column is visually quiet on rows
  // where it doesn't apply.
  { key: 'openInBrowser', label: '',         width: 32,  defaultVisible: true,  sortable: false },
  { key: 'intakeClass',  label: 'Intake',    width: 100, defaultVisible: true,  sortable: true },
  { key: 'tags',         label: 'Tags',      width: 260, defaultVisible: true,  sortable: false },
  // Container / Status / Health / Size tightened further (round 3 of
  // visual feedback): the badge content is short and the gap between
  // them was leaving too much air. Pair with reduced td side-padding
  // below to close it up.
  { key: 'container',    label: 'Container', width: 120, defaultVisible: true,  sortable: true },
  { key: 'status',       label: 'Status',    width: 80,  defaultVisible: true,  sortable: true },
  { key: 'health',       label: 'Health',    width: 70,  defaultVisible: true,  sortable: true },
  { key: 'sizeBytes',    label: 'Size',      width: 60,  defaultVisible: true,  sortable: true },
  { key: 'updatedAt',    label: 'Updated',   width: 160, defaultVisible: true,  sortable: true },
  { key: 'createdAt',    label: 'Created',   width: 110, defaultVisible: false, sortable: true },
  // CTSK-521 — the grouping labels. Default off; the cells are empty when
  // grouping is off for the org (the API leaves the fields out).
  { key: 'groupName',      label: 'Group',      width: 140, defaultVisible: false, sortable: false },
  { key: 'collectionName', label: 'Collection', width: 140, defaultVisible: false, sortable: false },
  { key: 'sourceFolder',   label: 'Folder',     width: 180, defaultVisible: false, sortable: false },
];

// Side-padding values applied per cell. The first column gets a small
// left-edge padding to match the tight right-edge spacing around the
// ellipsis ⋯ cell (matches the "same amount of space at the front"
// feedback). The compact-trio cells (container/status/health/size) get
// reduced horizontal padding so the badge cells sit closer together.
const FIRST_COL_PADDING_LEFT = 12;
const COMPACT_CELL_PADDING = '6px 6px';
const COMPACT_KEYS = new Set(['container', 'status', 'health', 'sizeBytes']);
const DOC_COL_STORE_KEY = 'tasko.documents.cols.v1';
// CTSK-521 — the columns a search hit can honour: GET /documents/search returns
// tags + updatedAt (and the group / collection / folder chips, which render
// whenever the hit carries them). Same prefs store as the list; the picker just
// offers this subset in search mode.
const SEARCH_COLUMN_KEYS: ReadonlySet<string> = new Set(['tags', 'updatedAt', 'intakeClass']);

const EMPTY_FILTERS: Filters = {
  q: '',
  tags: [],
  containerId: '',
  intakeClasses: [],
  statuses: [],
  health: [],
  dateFrom: '',
  dateTo: '',
};

// ?tag= URL mirror. Multiple tag ids are supported via repeated
// ?tag=<id>&tag=<id2> (matches how the API receives them). Read on
// mount, write back whenever filters.tags changes — so deep-links
// from the Tags page (and the documents list's own tag chips) land
// on a pre-filtered list.
function loadInitialTagsFromUrl(): string[] {
  if (typeof window === 'undefined') return [];
  const sp = new URLSearchParams(window.location.search);
  return sp.getAll('tag').filter(Boolean);
}
function writeTagsToUrl(tags: string[]) {
  if (typeof window === 'undefined') return;
  const sp = new URLSearchParams(window.location.search);
  sp.delete('tag');
  for (const t of tags) sp.append('tag', t);
  const next = `${window.location.pathname}?${sp.toString()}${window.location.hash}`;
  window.history.replaceState(null, '', next);
}

// 0.1.23 (Stage 2b of document-relationships FR) — ?related=<docId>
// URL mirror. When the operator clicks "Open in library" on the
// Related Documents panel, we land here pre-filtered to that source
// doc's outbound links.
function loadInitialRelatedFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const sp = new URLSearchParams(window.location.search);
  return sp.get('related');
}
function writeRelatedToUrl(docId: string | null) {
  if (typeof window === 'undefined') return;
  const sp = new URLSearchParams(window.location.search);
  if (docId) sp.set('related', docId); else sp.delete('related');
  const next = `${window.location.pathname}?${sp.toString()}${window.location.hash}`;
  window.history.replaceState(null, '', next);
}

// CTSK-498 — ?group= / ?collection= URL mirror (exact, case-insensitive match
// server-side). Deep-linked from the DocumentDetail Related panel.
// CTSK-500 — ?ingestBatch= (upload batch id) rides the same mirror.
// CTSK-501 — ?folder= (+ ?includeSubfolders=true) rides the same mirror.
function loadLabelFromUrl(key: 'group' | 'collection' | 'ingestBatch' | 'folder'): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(key);
}
function writeLabelToUrl(key: 'group' | 'collection' | 'ingestBatch' | 'folder', value: string | null) {
  if (typeof window === 'undefined') return;
  const sp = new URLSearchParams(window.location.search);
  if (value) sp.set(key, value); else sp.delete(key);
  window.history.replaceState(null, '', `${window.location.pathname}?${sp.toString()}${window.location.hash}`);
}

function loadSubfoldersFromUrl(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('includeSubfolders') === 'true';
}
function writeSubfoldersToUrl(value: boolean) {
  if (typeof window === 'undefined') return;
  const sp = new URLSearchParams(window.location.search);
  if (value) sp.set('includeSubfolders', 'true'); else sp.delete('includeSubfolders');
  window.history.replaceState(null, '', `${window.location.pathname}?${sp.toString()}${window.location.hash}`);
}

// Quick-filter chip row (ctsk-053). 'all' means the chip isn't applied;
// otherwise the chip activates a single, well-defined preset. The five
// chips were named by the COO in the spec — names preserved for
// consistency with master_tasks.yaml even where the implementation
// shorthand differs (e.g. "My nest" → "in a nest" semantics).
type QuickChip = 'all' | 'untagged' | 'nest' | 'recent' | 'review' | 'starred';

// ctsk-054 — page-size ladder per spec. Sized to cover small mobile lists
// (10) through the API hard cap (limit=500 ≈ 250 here is conservative;
// matches workitems' 200 step).
const PAGE_SIZES = [10, 25, 50, 100, 250] as const;
type PageSize = typeof PAGE_SIZES[number];
const PAGE_SIZE_STORE_KEY = 'tasko.documents.pageSize.v1';
const DEFAULT_PAGE_SIZE: PageSize = 25;

function loadPageSize(): PageSize {
  if (typeof window === 'undefined') return DEFAULT_PAGE_SIZE;
  const raw = window.localStorage.getItem(PAGE_SIZE_STORE_KEY);
  const n = raw ? Number(raw) : NaN;
  return (PAGE_SIZES as readonly number[]).includes(n) ? (n as PageSize) : DEFAULT_PAGE_SIZE;
}
function savePageSize(n: PageSize) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(PAGE_SIZE_STORE_KEY, String(n));
}

// URL-write helpers (ctsk-054). Page lives in the URL so deep-linking
// "/documents?page=3" works; pageSize also persists to localStorage so
// returning to the page keeps the workflow choice but the URL stays
// shareable.
function loadInitialPageFromUrl(): number {
  if (typeof window === 'undefined') return 1;
  const sp = new URLSearchParams(window.location.search);
  const n = Number(sp.get('page'));
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
}
function loadInitialLimitFromUrl(fallback: PageSize): PageSize {
  if (typeof window === 'undefined') return fallback;
  const sp = new URLSearchParams(window.location.search);
  const n = Number(sp.get('limit'));
  return (PAGE_SIZES as readonly number[]).includes(n) ? (n as PageSize) : fallback;
}
// CTSK-493 — ?q= mirror. The header DocumentSearch's Enter-with-no-row
// and "open full search" both land on /documents?q=<query> (design doc
// §3.8). Read on mount + whenever the Router's qParam prop changes, write
// back on edit so the URL stays shareable. CTSK-494 Phase 1c: with the
// document-search flag on, a q of ≥2 chars switches the page into search
// mode (GET /documents/search, highlight rows — see `searchMode` below);
// flag off, or a 1-char q, keeps the title-ILIKE list exactly as before.
function loadInitialQFromUrl(): string {
  const sp = new URLSearchParams(window.location.search);
  return sp.get('q') ?? '';
}

function writePageToUrl(page: number, pageSize: number) {
  if (typeof window === 'undefined') return;
  const sp = new URLSearchParams(window.location.search);
  sp.set('page', String(page));
  sp.set('limit', String(pageSize));
  const next = `${window.location.pathname}?${sp.toString()}${window.location.hash}`;
  window.history.replaceState(null, '', next);
}

const RECENT_DAYS = 7;

export function DocumentLibrary({
  onNavigate,
  nestParam,
  qParam,
}: {
  onNavigate: (p: string) => void;
  /** ctsk-056 — current value of ?nest= in the URL, piped through from
   *  the Router so sidebar-driven nest switches actually re-sync this
   *  page's nest filter. Null when no nest is active. */
  nestParam?: string | null;
  /** CTSK-493 — current value of ?q= in the URL (header search's
   *  full-page fallback). Null when absent. */
  qParam?: string | null;
}) {
  const qc = useQueryClient();
  const { session } = useSession();
  // 2026-06-03 — bulk select + Archive/Delete. Archive uses the
  // existing soft-delete (documents:delete cap). Delete uses
  // ?force=true for the hard-purge path on the same endpoint, gated
  // by the documents:purge cap (owner-only by default). Less-
  // privileged users see only the Archive button.
  const canPurge = (session?.capabilities ?? []).includes('documents:purge');
  // selectionMode gates the row + header checkboxes — disabled (greyed
  // out, not clickable) until the operator clicks "Select" to enter
  // multi-select mode. Click "Done" or finish a bulk action to exit.
  // Default off so the docs list reads as a navigation surface first,
  // a triage surface second.
  const [selectionMode, setSelectionMode] = useState(false);
  // CTSK-525 — card views. Saved mode is per user+org; below 900px the list is
  // forced without touching the saved preference. Expanded ids live here so they
  // survive filter changes within the session (never persisted, never bulk-set).
  const viewUser = session?.user?.id ?? null;
  const viewOrg = session?.activeOrgId ?? null;
  const [savedViewMode, setSavedViewMode] = useState<ViewMode>(() => loadViewMode(viewUser, viewOrg));
  const [cardCols, setCardCols] = useState<ColsPref>(() => loadCols(viewUser, viewOrg));
  const [previewAll, setPreviewAll] = useState<boolean>(() => loadPreviewAll(viewUser, viewOrg));
  const [highlightId, setHighlightId] = useState<string | null>(() => takeLastOpened());
  const highlightEffect = useMemo(() => loadHighlightEffect(), []);
  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => setHighlightId(null), 8000);
    return () => clearTimeout(t);
  }, [highlightId]);
  const cardActions = useMemo(() => ({
    onWatch: (d: DocumentListItem) => {
      apiSingle(`/documents/${d.id}/watch`, { method: d.isWatchedByMe ? 'DELETE' : 'POST' })
        .then(() => qc.invalidateQueries({ queryKey: ['documents'] }))
        .catch((e: Error) => toast.error(e.message));
    },
    onPipeline: (d: DocumentListItem) => { rememberOpened(d.id); onNavigate(`/documents/${d.id}#pipeline-output`); },
    onFindMore: (d: DocumentListItem) => {
      setFilters((f) => ({ ...f, tags: d.tags.map((t) => t.id) }));
      setQuickChip('all');
      setPage(1);
      toast.success(`Filtered by ${d.tags.length} tag${d.tags.length === 1 ? '' : 's'} from "${d.title}"`);
    },
    onTimelane: (d: DocumentListItem) => { rememberOpened(d.id); onNavigate('/tools/timeline-lane'); },
    onPostNote: (d: DocumentListItem) => {
      const text = window.prompt(`Note on "${d.title}":`);
      if (!text || !text.trim()) return;
      apiSingle('/notes', { method: 'POST', body: { body: text.trim(), targetType: 'document', targetId: d.id } })
        .then(() => toast.success('Note posted'))
        .catch((e: Error) => toast.error(e.message));
    },
    onPostComment: (d: DocumentListItem) => { rememberOpened(d.id); onNavigate(`/documents/${d.id}?tab=activity`); },
    onCopyLink: (d: DocumentListItem) => {
      void copyToClipboard(`${window.location.origin}/documents/${d.id}`).then(() => toast.success('Link copied'));
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [qc]);
  const [scaledPreview, setScaledPreview] = useState<boolean>(() => loadScaled(viewUser, viewOrg));
  useEffect(() => {
    setSavedViewMode(loadViewMode(viewUser, viewOrg));
    setCardCols(loadCols(viewUser, viewOrg));
    setPreviewAll(loadPreviewAll(viewUser, viewOrg));
    setScaledPreview(loadScaled(viewUser, viewOrg));
  }, [viewUser, viewOrg]);
  const wideViewport = useMediaQuery('(min-width: 900px)', true);
  const viewMode = effectiveViewMode(savedViewMode, wideViewport);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(() => new Set());
  function chooseViewMode(m: ViewMode) {
    setSavedViewMode(m);
    saveViewMode(m, viewUser, viewOrg);
  }
  function toggleCard(id: string) {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  function enterSelectionMode() {
    setSelectionMode(true);
    setSelectedIds(new Set());
  }
  function exitSelectionMode() {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }

  // Seed filters.tags from ?tag= so a deep-link like
  // /documents?tag=<id> lands pre-filtered. Other filter fields stay
  // out of the URL for now (the existing nest/page/limit mirror is
  // enough for the chip-click and Tags-page deep-link flows).
  const [filters, setFilters] = useState<Filters>(() => ({
    ...EMPTY_FILTERS,
    q: loadInitialQFromUrl(),
    tags: loadInitialTagsFromUrl(),
  }));
  // CTSK-493 — re-sync the title filter when the header search navigates
  // to /documents?q=… while this page is already mounted (pushState-only,
  // same lesson as ?nest=).
  useEffect(() => {
    if (qParam == null) return;
    setFilters((f) => (f.q === qParam ? f : { ...f, q: qParam }));
  }, [qParam]);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (filters.q) sp.set('q', filters.q); else sp.delete('q');
    const qs = sp.toString();
    const next = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
    if (next !== `${window.location.pathname}${window.location.search}`) {
      window.history.replaceState(null, '', next);
    }
  }, [filters.q]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Quick chip (ctsk-053). Client-side semantics — the chip narrows the
  // server page rather than tightening the query. Page-size cap keeps
  // this honest for the common case.
  const [quickChip, setQuickChip] = useState<QuickChip>('all');

  // Nest filter (ctsk-056). Stored as a slug; the actual server-side
  // query happens through `filters.tags` after we resolve the slug to
  // the master `nest/<slug>` tag id below.
  //
  // Seeded from the `nestParam` prop (the Router's parsed ?nest=) so
  // the URL is the canonical source of truth. The useEffect below
  // re-syncs whenever the prop changes — that's what fixes the bug
  // where sidebar nest-link clicks didn't update the page filter
  // (the component stays mounted across same-pathname navigations
  // so a lazy initializer alone wouldn't re-run).
  const [nestSlug, setNestSlug] = useState<string | null>(nestParam ?? null);
  useEffect(() => {
    setNestSlug(nestParam ?? null);
  }, [nestParam]);

  // CTSK-494 Phase 1c — search mode. Runtime flag from /meta/features
  // (Vite mirror as fallback, see lib/features.ts). The API rejects
  // queries under DOCUMENT_SEARCH_QUERY_MIN, so a 1-char q stays on the
  // list path rather than round-tripping a 400. Flag off → list mode even
  // with q present (the /documents/search route is unmounted then).
  const featureFlags = useFeatures();
  const searchQuery = filters.q.trim().slice(0, DOCUMENT_SEARCH_QUERY_MAX);
  const searchable = searchQuery.length >= DOCUMENT_SEARCH_QUERY_MIN;
  const searchMode = featureFlags.documentSearch && searchable;
  // With a searchable q, neither endpoint is called until /meta/features
  // has answered — otherwise the Vite fallback could fire a title-ILIKE
  // page that is replaced by FTS hits a moment later (one wasted request
  // + a visible swap). Without q there is nothing to decide: list mode.
  const modeKnown = !searchable || featureFlags.resolved;

  // ctsk-051 — Watching filter. SERVER-side via /documents?watchedBy=…
  // (changes the page result set, not just a client-side narrow), so
  // pagination math stays accurate. 'all' omits the param.
  const [watchFilter, setWatchFilter] = useState<'all' | 'mine' | 'not-mine'>('all');

  // 0.1.23 (Stage 2b) — related-to filter. Server-side via
  // /documents?related=<docId>. Mirrored to URL.
  const [relatedDocId, setRelatedDocId] = useState<string | null>(loadInitialRelatedFromUrl);
  // CTSK-498 — group/collection filters + Bulk Edit palette. Everything is
  // inert (no query params sent, no button) unless grouping is live for the org.
  const grouping = useDocumentGrouping();
  const [groupFilter, setGroupFilter] = useState<string | null>(() => loadLabelFromUrl('group'));
  const [collectionFilter, setCollectionFilter] = useState<string | null>(() => loadLabelFromUrl('collection'));
  const [batchFilter, setBatchFilter] = useState<string | null>(() => loadLabelFromUrl('ingestBatch'));
  // CTSK-501 — folder filter (exact, or with subfolders); case-sensitive server-side.
  const [folderFilter, setFolderFilter] = useState<string | null>(() => loadLabelFromUrl('folder'));
  const [subfoldersFilter, setSubfoldersFilter] = useState<boolean>(loadSubfoldersFromUrl);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  useEffect(() => { writeLabelToUrl('folder', folderFilter); }, [folderFilter]);
  useEffect(() => { writeSubfoldersToUrl(subfoldersFilter && !!folderFilter); }, [subfoldersFilter, folderFilter]);
  useEffect(() => { writeLabelToUrl('ingestBatch', batchFilter); }, [batchFilter]);
  useEffect(() => { writeLabelToUrl('group', groupFilter); }, [groupFilter]);
  useEffect(() => { writeLabelToUrl('collection', collectionFilter); }, [collectionFilter]);
  const activeGroup = grouping.enabled ? groupFilter : null;
  const activeCollection = grouping.enabled ? collectionFilter : null;
  // CTSK-500 — shown/sent only while grouping is live (the API ignores it otherwise).
  const activeBatch = grouping.enabled ? batchFilter : null;
  // CTSK-501 — same gate: nothing is shown or sent while grouping is off.
  const activeFolder = grouping.enabled ? folderFilter : null;
  const activeSubfolders = activeFolder ? subfoldersFilter : false;
  // Fetch the source doc's title for the chip display.
  const relatedDocQ = useQuery({
    queryKey: ['document-detail-title', relatedDocId],
    queryFn: () => apiSingle<DocumentListItem>(`/documents/${relatedDocId}`),
    enabled: !!relatedDocId,
    staleTime: 60_000,
  });
  useEffect(() => { writeRelatedToUrl(relatedDocId); }, [relatedDocId]);

  // ctsk-276 — browser-back should restore URL-mirrored filter state.
  //
  // Every URL-write here uses history.replaceState (overwrites the current
  // entry, no new history push), and the state initializers use useState
  // lazy factories that only run once at mount. When the router keeps this
  // component mounted across same-pathname navigations (e.g. tag chip
  // click → URL change → back-nav), the state doesn't re-read from URL
  // and filters appear to "reset" from the operator's viewpoint.
  //
  // Fix: single popstate handler that re-syncs every URL-mirrored piece
  // of state. `nestSlug` is already handled via the router-fed nestParam
  // prop + its own useEffect, so it's deliberately excluded here.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    function onPop() {
      setFilters((prev) => ({ ...prev, tags: loadInitialTagsFromUrl() }));
      setRelatedDocId(loadInitialRelatedFromUrl());
      setPage(loadInitialPageFromUrl());
      setPageSize(loadInitialLimitFromUrl(loadPageSize()));
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // ctsk-061 — column config (show/hide/reorder, persisted to ls).
  const { prefs: colPrefs, setPrefs: setColPrefs, visibleCols } = useColPrefs(DOC_COL_STORE_KEY, DOC_COLUMNS);

  // Pagination state (ctsk-054). page in URL; pageSize in URL + ls.
  const [pageSize, setPageSize] = useState<PageSize>(() => loadInitialLimitFromUrl(loadPageSize()));
  const [page, setPage] = useState(loadInitialPageFromUrl);

  // Persist page-size to ls + URL whenever it changes.
  useEffect(() => { savePageSize(pageSize); }, [pageSize]);
  // Write page+limit to URL.
  useEffect(() => { writePageToUrl(page, pageSize); }, [page, pageSize]);
  // Any filter change → jump back to page 1.
  useEffect(() => { setPage(1); }, [
    filters.q, filters.tags, filters.containerId,
    filters.intakeClasses, filters.statuses, filters.health,
    filters.dateFrom, filters.dateTo, quickChip, pageSize, nestSlug, watchFilter,
    relatedDocId, activeGroup, activeCollection, activeBatch, activeFolder, activeSubfolders,
  ]);

  // Mirror the active nest into the URL alongside ?page= & ?limit=, so
  // sidebar shortcuts can deep-link to /documents?nest=<slug>.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sp = new URLSearchParams(window.location.search);
    if (nestSlug) sp.set('nest', nestSlug); else sp.delete('nest');
    const next = `${window.location.pathname}?${sp.toString()}${window.location.hash}`;
    window.history.replaceState(null, '', next);
  }, [nestSlug]);

  // Mirror the active tag filter into the URL so the chip-click + the
  // Tags admin deep-link produce a shareable /documents?tag=<id>.
  useEffect(() => {
    writeTagsToUrl(filters.tags);
  }, [filters.tags]);

  const tagsQ = useQuery({
    queryKey: ['tags'],
    queryFn: () => apiList<Tag>('/tags'),
  });
  const containersQ = useQuery({
    queryKey: ['storage-containers'],
    queryFn: () => apiList<StorageContainer>('/storage-containers'),
  });

  // ctsk-056 — resolve the active nest slug to the master tag id once
  // the tags list is loaded. The master tag follows the convention
  // `nest/<slug>` (per docs/nests/README.md) so this is a simple lookup.
  const nestTagId = useMemo(() => {
    if (!nestSlug) return null;
    const match = (tagsQ.data?.data ?? []).find((t) => t.name === `nest/${nestSlug}`);
    return match?.id ?? null;
  }, [nestSlug, tagsQ.data?.data]);

  // CTSK-522 — hide-by-default nests (external-clients) with chip reveal.
  const hiddenNests = useHiddenNests({ activeNestSlug: nestSlug, tags: tagsQ.data?.data });
  const nestSlugOfDoc = (d: DocumentListItem): string | null => {
    const t = d.tags.find((x) => x.name.startsWith('nest/'));
    return t ? t.name.slice('nest/'.length) : null;
  };
  const isHiddenDoc = (d: DocumentListItem): boolean => {
    const slug = nestSlugOfDoc(d);
    return slug !== null && hiddenNests.hiddenSet.has(slug);
  };

  // The composed tag list sent to the server is filters.tags ∪ {nestTagId}.
  // We pass them as two args so the user can clear nest independently of
  // any tags they picked from the advanced panel.
  const composedTagIds = useMemo(() => {
    const set = new Set(filters.tags);
    if (nestTagId) set.add(nestTagId);
    return Array.from(set);
  }, [filters.tags, nestTagId]);

  // ctsk-051 — map the segmented watch filter to ?watchedBy. 'all'
  // omits the param so the server returns the full page.
  const watchedByParam =
    watchFilter === 'mine' ? 'me' : watchFilter === 'not-mine' ? 'not-me' : undefined;

  // CTSK-494 — /documents/search caps `limit` at DOCUMENT_SEARCH_LIMIT_MAX;
  // the list's 250 rows/page is clamped in search mode and the page-size
  // menu only offers sizes the search endpoint accepts.
  const searchPageSize = Math.min(pageSize, DOCUMENT_SEARCH_LIMIT_MAX);
  const effectivePageSize = searchMode ? searchPageSize : pageSize;
  const pageSizeOptions = searchMode
    ? PAGE_SIZES.filter((n) => n <= DOCUMENT_SEARCH_LIMIT_MAX)
    : PAGE_SIZES;

  const listQ = useQuery({
    queryKey: ['documents', filters, nestTagId, watchedByParam, relatedDocId, activeGroup, activeCollection, activeBatch, activeFolder, activeSubfolders, page, pageSize],
    enabled: modeKnown && !searchMode,
    queryFn: () =>
      apiList<DocumentListItem>('/documents', {
        query: {
          q: filters.q || undefined,
          tag: composedTagIds.length ? composedTagIds : undefined,
          containerId: filters.containerId || undefined,
          watchedBy: watchedByParam,
          related: relatedDocId || undefined,
          group: activeGroup || undefined,
          collection: activeCollection || undefined,
          ingestBatch: activeBatch || undefined,
          folder: activeFolder || undefined,
          includeSubfolders: activeFolder && activeSubfolders ? true : undefined,
          intakeClass: filters.intakeClasses.length ? filters.intakeClasses : undefined,
          status: filters.statuses.length ? filters.statuses : undefined,
          health: filters.health.length ? filters.health : undefined,
          dateFrom: filters.dateFrom ? new Date(filters.dateFrom).toISOString() : undefined,
          dateTo: filters.dateTo ? new Date(filters.dateTo + 'T23:59:59').toISOString() : undefined,
          // Switch the API to offset-mode by passing offset (even =0).
          // Cursor mode is what hyper-scanno + tasko_push use; the UI
          // wants page-stable navigation with a true total.
          limit: pageSize,
          offset: (page - 1) * pageSize,
        },
      }),
    placeholderData: (prev) => prev, // keep old page during refetch — no flicker
  });

  // CTSK-494 Phase 1c — the FTS call. `page`/`limit` map straight onto the
  // list's page state (the list talks offset/limit to /documents; the
  // search endpoint is page/limit, so no offset math here). `nest` is the
  // same slug the chip + ?nest= already hold. No auto-retry (design doc
  // §3.7) — the error state owns Retry.
  const searchQ = useQuery({
    // CTSK-507 — the label filters ride along (group / collection / folder, +subfolders);
    // each is undefined (not sent) unless grouping is live and the filter is set.
    queryKey: [
      'document-search', 'page', searchQuery, nestSlug, searchPageSize, page,
      activeGroup, activeCollection, activeFolder, activeSubfolders,
    ],
    enabled: modeKnown && searchMode,
    retry: false,
    queryFn: ({ signal }) =>
      apiFetch<DocumentSearchResponse>('/documents/search', {
        query: {
          q: searchQuery, limit: searchPageSize, page, nest: nestSlug ?? undefined,
          group: activeGroup || undefined,
          collection: activeCollection || undefined,
          folder: activeFolder || undefined,
          includeSubfolders: activeFolder && activeSubfolders ? true : undefined,
        },
        signal,
      }),
    placeholderData: (prev) => prev,
  });

  // The flag can say "on" while the API has since been restarted with it
  // off: /documents/search is then unmounted and the request falls through
  // to GET /documents/:id → 404 NOT_FOUND (not NEST_NOT_FOUND, which is a
  // real 404 from the mounted route). Treat that as "search is off" for
  // the whole page load — flip the cached flag so this page drops to list
  // mode and the header reverts too — instead of showing an error.
  useEffect(() => {
    const err = searchQ.error;
    if (!(err instanceof ApiError) || err.status !== 404 || err.code === 'NEST_NOT_FOUND') return;
    qc.setQueryData(FEATURES_QUERY_KEY, (prev: { documentSearch: boolean } | undefined) => ({
      ...(prev ?? {}),
      documentSearch: false,
    }));
  }, [searchQ.error, qc]);

  const data = searchMode ? undefined : listQ.data;
  const isLoading = !modeKnown || (searchMode ? searchQ.isLoading : listQ.isLoading);
  const isError = searchMode ? searchQ.isError : listQ.isError;
  const searchResults = searchMode
    ? (searchQ.data?.data.results ?? []).filter((r) => !(r.nest && hiddenNests.hiddenSet.has(r.nest.slug)))
    : [];
  // CTSK-525 — client-side sort of the CURRENT page of hits (the server ranks by relevance).
  const [searchSort, setSearchSort] = useState<{ key: 'relevance' | 'title' | 'updatedAt' | 'docType' | 'nest'; dir: 'asc' | 'desc' }>({ key: 'relevance', dir: 'desc' });
  const sortedResults = useMemo(() => {
    if (searchSort.key === 'relevance') return searchResults;
    const val = (r: (typeof searchResults)[number]): string =>
      searchSort.key === 'title' ? r.title.toLowerCase()
        : searchSort.key === 'updatedAt' ? r.updatedAt
          : searchSort.key === 'docType' ? String(r.docType)
            : (r.nest?.slug ?? '~');
    const out = [...searchResults].sort((x, y) => (val(x) < val(y) ? -1 : val(x) > val(y) ? 1 : 0));
    return searchSort.dir === 'desc' ? out.reverse() : out;
  }, [searchResults, searchSort]);
  const hitById = useMemo(() => new Map(sortedResults.map((r) => [r.id, r])), [sortedResults]);
  const hitDocs = useMemo(
    () => sortedResults.map((r) => ({
      id: r.id, title: r.title, mimeType: '', sizeBytes: 0, createdAt: r.updatedAt, updatedAt: r.updatedAt,
      tags: [], isWatchedByMe: false,
    }) as unknown as DocumentListItem),
    [sortedResults],
  );
  // CTSK-507 — a result chip or a strip entry adds that filter (stored value). The
  // existing effects mirror it to the URL; changing a filter resets the page.
  const addLabelFilter = (kind: LabelKind, value: string) => {
    if (kind === 'group') setGroupFilter(value);
    else if (kind === 'collection') setCollectionFilter(value);
    else { setFolderFilter(value); setSubfoldersFilter(false); }
  };

  const total = searchMode ? (searchQ.data?.data.total ?? 0) : (data?.page?.total ?? 0);
  const totalPages = Math.max(1, Math.ceil(total / effectivePageSize));
  // Guard against ending up beyond totalPages after filter changes. Only
  // once the active query has answered — before that `total` is 0 and a
  // deep-linked ?page=3 would be clamped back to 1 (CTSK-494).
  const hasAnswer = searchMode ? searchQ.data !== undefined : data !== undefined;
  useEffect(() => {
    if (hasAnswer && page > totalPages) setPage(totalPages);
  }, [hasAnswer, page, totalPages]);

  useRealtime({
    types: ['doc.created', 'doc.updated', 'doc.deleted', 'doc.restored', 'doc.processing_completed'],
    onEvent: () => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['document-search', 'page'] });
    },
  });

  const upload = useBulkUpload({
    onGroupApplied: () => { qc.invalidateQueries({ queryKey: ['documents'] }); },
    onSettled: ({ ok, failed }) => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      if (ok > 0 && failed === 0) toast.success(`Uploaded ${ok} document${ok === 1 ? '' : 's'}`);
      else if (ok > 0 && failed > 0) toast.error(`Uploaded ${ok}, ${failed} failed`);
      else if (failed > 0) toast.error(`All ${failed} upload${failed === 1 ? '' : 's'} failed`);
    },
  });

  const [dragOver, setDragOver] = useState(false);

  // CTSK-501 — directory drops and the folder picker. Nothing uploads until the
  // confirmation dialog is accepted. The dropped entries are collected
  // SYNCHRONOUSLY in the drop handler (the item list is invalid after the first
  // await); only the walk below is async.
  const [folderDrop, setFolderDrop] = useState<FolderDropSummary | null>(null);
  const [folderReading, setFolderReading] = useState(false);
  function openFolderDialog(walk: WalkResult) {
    setFolderDrop(summarizeDrop(walk, { maxFilesPerDrop: grouping.groupingMaxFilesPerDrop }));
  }
  async function handleDirectoryDrop(collected: CollectedDrop) {
    setFolderReading(true);
    try {
      const walk = await readDroppedEntries(collected.entries, { ignore: grouping.groupingIgnore });
      for (const f of collected.looseFiles) walk.files.push({ file: f, sourceFolder: null });
      openFolderDialog(walk);
    } catch {
      toast.error('Could not read the dropped folder.');
    } finally {
      setFolderReading(false);
    }
  }
  function handleFolderPick(files: File[]) {
    openFolderDialog(filesFromPicker(files, { ignore: grouping.groupingIgnore }));
  }

  // Apply the quick-filter chip on top of the server result. Five chips
  // per the spec (Untagged / In a nest / Recent / Needs review / Starred
  // tags). Starred is parked behind a coming-soon tooltip until the
  // star-a-tag infra exists.
  const filteredRows = useMemo(() => {
    const rows = (data?.data ?? []).filter((d) => !isHiddenDoc(d));
    if (quickChip === 'all') return rows;
    const recentCutoff = Date.now() - RECENT_DAYS * 86_400_000;
    return rows.filter((d) => {
      switch (quickChip) {
        case 'untagged':
          return d.tagCount === 0;
        case 'nest':
          // Any tag named like the master `nest/<slug>` convention.
          return d.tags.some((t) => t.name.startsWith('nest/'));
        case 'recent':
          return new Date(d.createdAt).getTime() >= recentCutoff;
        case 'review':
          // "Needs review" intent: still in the pre-review pipeline
          // (captured/parsed/enriched but not yet 'reviewed') OR
          // explicitly flagged as warning/degraded health.
          return d.status === 'captured'
            || d.status === 'parsed'
            || d.status === 'enriched'
            || d.health === 'warning'
            || d.health === 'degraded';
        case 'starred':
          // No starring infra yet — chip is disabled below.
          return true;
        default:
          return true;
      }
    });
  }, [data?.data, quickChip, hiddenNests.hiddenSet]);

  // Narrow the Tags filter (in "Show filters") to just the tags
  // actually carried by documents in the current view, plus any tag
  // the user has already selected (so deselecting it remains possible
  // even if the active filters move it off-screen). The full org tag
  // list can be hundreds of entries long — this collapses the noise
  // and surfaces the relevant subset for the active query.
  //
  // Sort order: selected first (sticky), then by occurrence count
  // desc, then by name asc. Count is shown in the chip label so the
  // user can see how much each filter would narrow further.
  const tagsInUseInView = useMemo(() => {
    const counts = new Map<string, { tag: Tag; count: number }>();
    for (const d of filteredRows) {
      for (const t of d.tags) {
        const prev = counts.get(t.id);
        if (prev) prev.count += 1;
        else counts.set(t.id, { tag: t, count: 1 });
      }
    }
    // Ensure currently-selected tags stay visible even if 0 docs on
    // this page carry them — otherwise the user can't deselect.
    const allTags = tagsQ.data?.data ?? [];
    for (const selectedId of filters.tags) {
      if (!counts.has(selectedId)) {
        const found = allTags.find((t) => t.id === selectedId);
        if (found) counts.set(found.id, { tag: found, count: 0 });
      }
    }
    const selectedSet = new Set(filters.tags);
    return Array.from(counts.values()).sort((a, b) => {
      const aSel = selectedSet.has(a.tag.id) ? 1 : 0;
      const bSel = selectedSet.has(b.tag.id) ? 1 : 0;
      if (aSel !== bSel) return bSel - aSel;
      if (a.count !== b.count) return b.count - a.count;
      return a.tag.name.localeCompare(b.tag.name);
    });
  }, [filteredRows, filters.tags, tagsQ.data?.data]);

  const totalOrgTagCount = tagsQ.data?.data.length ?? 0;
  const hiddenTagCount = Math.max(0, totalOrgTagCount - tagsInUseInView.length);

  const { sorted, sort, setSort } = useTableSort(filteredRows, {
    // CTSK-246 — star column sort: watched=1 / not=0, desc puts
    // watched docs first. Mirrors WorkItemsList's `watch` accessor.
    watch: (d: DocumentListItem) => (d.isWatchedByMe ? 1 : 0),
    title: (d: DocumentListItem) => d.title,
    intakeClass: (d) => d.intakeClass,
    container: (d) => d.containers[0]?.name ?? '~',
    status: (d) => d.status,
    health: (d) => d.health,
    sizeBytes: (d) => d.sizeBytes,
    updatedAt: (d) => d.updatedAt,
  }, { key: 'updatedAt', dir: 'desc' });
  const cardIds = searchMode ? sortedResults.map((r) => r.id) : sorted.map((d) => d.id);
  const cardCount = cardIds.length;
  const showCards = viewMode !== 'list';

  // ─── Bulk selection helpers + mutations ─────────────────────────
  //
  // Master "select all" only covers the currently-visible page so the
  // operator never accidentally archives a doc they can't see. Clear
  // selection on any filter or page change so stale row IDs don't
  // linger.
  // CTSK-521 — in search mode the visible page is the hit list, not the table.
  const searchHits = searchQ.data?.data.results;
  const visibleIds = useMemo(
    () => (searchMode ? (searchHits ?? []).map((r) => r.id) : sorted.map((d) => d.id)),
    [searchMode, searchHits, sorted],
  );
  const allVisibleSelected = visibleIds.length > 0
    && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));

  function toggleOne(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAllVisible() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }
  function clearSelection() { setSelectedIds(new Set()); }

  // Clear selection when the visible page changes (filters, pagination,
  // nest, watch filter, etc.). Stops a selected ID set from leaking
  // across pages where the operator can't see what they've picked.
  useEffect(() => { setSelectedIds(new Set()); }, [
    filters.q, filters.tags, filters.containerId,
    filters.intakeClasses, filters.statuses, filters.health,
    filters.dateFrom, filters.dateTo, quickChip, page, pageSize,
    nestSlug, watchFilter, activeGroup, activeCollection, activeBatch, activeFolder, activeSubfolders,
  ]);

  // CTSK-498 — POST /documents/bulk-edit. Partial success is normal: the toast
  // reports counts, failed/skipped ids stay selected for a retry, and the
  // selection is only dropped when every row applied.
  const bulkEdit = useMutation({
    mutationFn: (mutations: BulkEditMutations) =>
      apiSingle<DocumentBulkEditResponse>('/documents/bulk-edit', {
        method: 'POST',
        body: { ids: [...selectedIds], mutations },
      }),
    onSuccess: ({ summary, results }) => {
      const failed = summary.failed + summary.skipped;
      const text = `${summary.succeeded} updated, ${failed} failed`;
      if (failed === 0) toast.success(text); else toast.error(text);
      setBulkEditOpen(false);
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['document-search'] }); // CTSK-521 — search results re-query after a bulk action
      if (failed === 0) exitSelectionMode();
      else setSelectedIds(new Set(results.filter((x) => !x.applied).map((x) => x.id)));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // CTSK-518 — one POST /documents/bulk-archive call instead of N per-id
  // DELETEs. Same toasts + selection behavior as the old loop; `failed`
  // now comes from the server's per-row summary instead of allSettled.
  const archiveBulk = useMutation({
    mutationFn: (ids: string[]) =>
      apiSingle<DocumentBulkEditResponse>('/documents/bulk-archive', {
        method: 'POST',
        body: { ids },
      }),
    onSuccess: ({ summary }) => {
      const { total, succeeded } = summary;
      const failed = total - succeeded;
      if (failed === 0) toast.success(`Archived ${total} document${total === 1 ? '' : 's'}`);
      else toast.error(`${succeeded}/${total} archived; ${failed} failed`);
      exitSelectionMode();
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['document-search'] }); // CTSK-521 — search results re-query after a bulk action
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // CTSK-518 — one POST /documents/bulk-purge call (the batch twin of
  // DELETE /documents/:id?force=true; "Delete" in this bulk bar has always
  // been the hard purge, documents:purge-gated).
  const deleteBulk = useMutation({
    mutationFn: (ids: string[]) =>
      apiSingle<DocumentBulkEditResponse>('/documents/bulk-purge', {
        method: 'POST',
        body: { ids },
      }),
    onSuccess: ({ summary }) => {
      const { total, succeeded } = summary;
      const failed = total - succeeded;
      if (failed === 0) toast.success(`Permanently deleted ${total} document${total === 1 ? '' : 's'}`);
      else toast.error(`${succeeded}/${total} deleted; ${failed} failed`);
      exitSelectionMode();
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['document-search'] }); // CTSK-521 — search results re-query after a bulk action
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const activeFilterCount =
    (filters.q ? 1 : 0) +
    filters.tags.length +
    (filters.containerId ? 1 : 0) +
    filters.intakeClasses.length +
    filters.statuses.length +
    filters.health.length +
    (filters.dateFrom ? 1 : 0) +
    (filters.dateTo ? 1 : 0) +
    (quickChip !== 'all' ? 1 : 0) +
    (nestSlug ? 1 : 0) +
    (watchFilter !== 'all' ? 1 : 0) +
    (activeGroup ? 1 : 0) +
    (activeCollection ? 1 : 0) +
    (activeBatch ? 1 : 0) +
    (activeFolder ? 1 : 0);

  return (
    <div
      onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
      onDragOver={(e) => { e.preventDefault(); }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        // CTSK-501 — read EVERYTHING off the DataTransfer before any await.
        const files = e.dataTransfer.files;
        const collected = collectDroppedItems(e.dataTransfer.items, files);
        if (collected.hasDirectory) {
          void handleDirectoryDrop(collected);
          return;
        }
        if (files && files.length > 0) upload.enqueue(files);
      }}
    >
      <div className="page-header">
        <div>
          <h1 className="page-title">Documents</h1>
          <div className="page-subtitle" data-testid="documents-subtitle">
            {searchMode ? (
              <>
                {!searchQ.data
                  ? `Searching for “${searchQuery}”…`
                  : total === 0
                  ? `No results for “${searchQuery}”`
                  : `Showing ${(page - 1) * effectivePageSize + 1}–${Math.min(page * effectivePageSize, total)} of ${total.toLocaleString()} result${total === 1 ? '' : 's'} for “${searchQuery}”`}
                {searchQ.data?.meta.nest && <span> · in {searchQ.data.meta.nest.name}</span>}
                {searchQ.data && <span> · {searchQ.data.meta.elapsedMs} ms</span>}
              </>
            ) : (
              <>
                {total === 0
                  ? '0 documents'
                  : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total.toLocaleString()}`}
                {activeFilterCount > 0 && <span> · {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'} active</span>}
              </>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label className="btn btn--create">
            <input
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length > 0) upload.enqueue(files);
                e.target.value = '';
              }}
            />
            + Upload {upload.queue.length > 0 ? `(${upload.queuedCount + upload.activeCount} in queue)` : 'documents'}
          </label>
          {/* CTSK-501 — sibling of the upload button, not nested under it. Only
              offered while grouping is live (a dropped folder still uploads flat when it is off). */}
          {grouping.enabled && (
            <label className="btn btn--create" data-testid="choose-folder-button">
              <input
                type="file"
                multiple
                data-testid="choose-folder-input"
                style={{ display: 'none' }}
                {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
                onChange={(e) => {
                  const files = e.target.files;
                  if (files && files.length > 0) handleFolderPick(Array.from(files));
                  e.target.value = '';
                }}
              />
              + Choose folder
            </label>
          )}
          {folderReading && <span role="status" style={{ fontSize: 12, color: 'var(--text-muted)' }}>Reading folder…</span>}
        </div>
      </div>

      {/* Quick-filter chip row (ctsk-053). CTSK-494: client-side chips
          narrow a list page; they have no meaning over ranked search
          hits, so search mode replaces the row with a one-line notice. */}
      {searchMode ? (
        <div
          className="doc-library__search-notice"
          data-testid="documents-search-mode-notice"
          role="status"
          style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}
        >
          Full-text search results, ranked by relevance. Quick filters and advanced
          filters are available when the search is cleared.
        </div>
      ) : (
        <QuickFilterChips
          value={quickChip}
          onChange={setQuickChip}
          recentDays={RECENT_DAYS}
        />
      )}

      {/* Nest filter chip row (ctsk-056) + Watching segmented (ctsk-051).
          Hides nest chip when the org has no nests so non-nest orgs
          don't see clutter. The nest chip stays in search mode — it is the
          `nest=` scope forwarded to /documents/search (CTSK-494). */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <NestFilterChip value={nestSlug} onChange={setNestSlug} />
        {!searchMode && <WatchSegmented value={watchFilter} onChange={setWatchFilter} />}
        {/* CTSK-525: list mode keeps this chip in the filters accordion; search mode has no accordion. */}
        {searchMode && <HiddenNestChips chips={hiddenNests.chips} onToggle={hiddenNests.toggle} />}
      </div>

      {/* Search + advanced toggle + reset */}
      <div className="toolbar" style={{ flexWrap: 'wrap' }}>
        <input
          type="search"
          placeholder="Search by title…"
          aria-label={searchMode ? 'Search documents (full text)' : 'Search by title'}
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          style={{ padding: '6px 12px', border: '1px solid var(--border-strong)', borderRadius: 6, flex: 1, maxWidth: 360 }}
        />
        {!searchMode && (
          <button className="btn btn--ghost" onClick={() => setShowAdvanced((v) => !v)}>
            {showAdvanced ? 'Hide filters' : 'Show filters'}
          </button>
        )}
        {activeFilterCount > 0 && (
          <button className="btn btn--ghost" onClick={() => { setFilters(EMPTY_FILTERS); setQuickChip('all'); setNestSlug(null); setWatchFilter('all'); setRelatedDocId(null); }}>
            Reset all
          </button>
        )}
        {/* ctsk-061 — column config dropdown. CTSK-521: also in search mode,
            offering the subset a hit can honour; one prefs store for both. */}
        <div style={{ width: 120, display: 'flex', flex: 'none' }}>{(searchMode || viewMode === 'list') && <ColumnsMenu
          columns={DOC_COLUMNS}
          prefs={colPrefs}
          onChange={setColPrefs}
          availableKeys={searchMode ? SEARCH_COLUMN_KEYS : undefined}
        />}</div>
        {searchMode && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 12, fontSize: 12 }}>
            <span style={{ color: 'var(--text-muted)' }}>Sort</span>
            <select
              aria-label="Sort results"
              value={searchSort.key}
              onChange={(e) => setSearchSort((s0) => ({ key: e.target.value as typeof s0.key, dir: e.target.value === 'title' || e.target.value === 'docType' || e.target.value === 'nest' ? 'asc' : 'desc' }))}
              style={{ padding: '4px 8px', border: '1px solid var(--border-strong)', borderRadius: 6 }}
            >
              <option value="relevance">Relevance</option>
              <option value="title">Title</option>
              <option value="updatedAt">Updated</option>
              <option value="docType">Type</option>
              <option value="nest">Nest</option>
            </select>
            {searchSort.key !== 'relevance' && (
              <button type="button" className="btn btn--ghost" aria-label="Toggle sort direction" style={{ padding: '2px 8px' }}
                onClick={() => setSearchSort((s0) => ({ ...s0, dir: s0.dir === 'asc' ? 'desc' : 'asc' }))}>
                {searchSort.dir === 'asc' ? '↑' : '↓'}
              </button>
            )}
          </label>
        )}
        {wideViewport && (
          <div style={{ marginLeft: 24 }}>
            <CardViewBar
              v={{
                wide: wideViewport, savedMode: savedViewMode, mode: viewMode, setMode: chooseViewMode,
                cols: cardCols, setCols: (c) => { setCardCols(c); saveCols(c, viewUser, viewOrg); },
                previewAll, setPreviewAll: (v) => { setPreviewAll(v); savePreviewAll(v, viewUser, viewOrg); },
                scaled: scaledPreview, setScaled: (v) => { setScaledPreview(v); saveScaled(v, viewUser, viewOrg); },
              }}
            />
          </div>
        )}
        <div style={{ flex: 1 }} />
        {/* 0.1.23 Stage 2b — active related-to chip. Visible when
            ?related=<docId> was used to deep-link from the
            Related Documents panel. Clicking the × clears it. */}
        {activeGroup && <LabelFilterChip kind="Group" value={activeGroup} onClear={() => setGroupFilter(null)} />}
        {activeCollection && <LabelFilterChip kind="Collection" value={activeCollection} onClear={() => setCollectionFilter(null)} />}
        {activeBatch && <LabelFilterChip kind="Batch" value={activeBatch} onClear={() => setBatchFilter(null)} />}
        {activeFolder && (
          <LabelFilterChip
            kind="Folder"
            value={activeFolder}
            includeSubfolders={activeSubfolders}
            onToggleSubfolders={setSubfoldersFilter}
            onClear={() => { setFolderFilter(null); setSubfoldersFilter(false); }}
          />
        )}
        {relatedDocId && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 999,
              fontSize: 12,
              background: 'color-mix(in srgb, var(--accent-primary) 14%, var(--surface-raised))',
              color: 'var(--accent-primary)',
              border: '1px solid color-mix(in srgb, var(--accent-primary) 40%, transparent)',
              maxWidth: 360,
            }}
            title={`Filtered to documents linked from ${relatedDocQ.data?.title ?? relatedDocId}`}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Related to: <strong>{relatedDocQ.data?.title ?? relatedDocId}</strong>
            </span>
            <button
              type="button"
              aria-label="Clear related-to filter"
              onClick={() => setRelatedDocId(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                fontSize: 14,
                padding: 0,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        )}
        {/* Compact pagination in the toolbar (ctsk-054), mirrors WorkItemsList */}
        <Pagination
          page={page}
          totalPages={totalPages}
          pageSize={effectivePageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(n) => setPageSize(n as PageSize)}
          pageSizes={pageSizeOptions}
          compact
        />
      </div>

      {showAdvanced && !searchMode && (
        <div
          style={{
            background: 'var(--surface-raised)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            padding: 16,
            marginBottom: 12,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 16,
          }}
        >
          {hiddenNests.chips.length > 0 && (
            <FilterPanel label="Hidden nests">
              <HiddenNestChips chips={hiddenNests.chips} onToggle={hiddenNests.toggle} />
            </FilterPanel>
          )}
          <FilterPanel
            label="Tags"
            hint={
              tagsInUseInView.length === 0 && totalOrgTagCount > 0
                ? `0 of ${totalOrgTagCount} in view`
                : hiddenTagCount > 0
                  ? `${tagsInUseInView.length} of ${totalOrgTagCount} in view · ${hiddenTagCount} hidden`
                  : undefined
            }
          >
            <MultiChip
              options={tagsInUseInView.map(({ tag, count }) => ({
                value: tag.id,
                // Append count so the user can see how much each tag
                // would narrow the result. Zero-count chips (kept
                // because they're already selected) show no count.
                label: count > 0 ? `${tag.name} · ${count}` : tag.name,
                dot: tag.color,
              }))}
              selected={filters.tags}
              onChange={(next) => setFilters((f) => ({ ...f, tags: next }))}
              empty={totalOrgTagCount === 0 ? 'No tags yet.' : 'No tags on the current results.'}
            />
          </FilterPanel>

          <FilterPanel label="Container">
            <select
              value={filters.containerId}
              onChange={(e) => setFilters((f) => ({ ...f, containerId: e.target.value }))}
              style={{ width: '100%', padding: 4 }}
            >
              <option value="">(any)</option>
              {(containersQ.data?.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </FilterPanel>

          <FilterPanel label="Intake class">
            <MultiChip
              options={INTAKE_CLASSES.map((c) => ({ value: c, label: c }))}
              selected={filters.intakeClasses}
              onChange={(next) => setFilters((f) => ({ ...f, intakeClasses: next as IntakeClass[] }))}
            />
          </FilterPanel>

          <FilterPanel label="Status">
            <MultiChip
              options={DOCUMENT_STATUSES.map((s) => ({ value: s, label: s }))}
              selected={filters.statuses}
              onChange={(next) => setFilters((f) => ({ ...f, statuses: next as DocumentStatus[] }))}
            />
          </FilterPanel>

          <FilterPanel label="Health">
            <MultiChip
              options={DOCUMENT_HEALTH.map((h) => ({ value: h, label: h }))}
              selected={filters.health}
              onChange={(next) => setFilters((f) => ({ ...f, health: next as DocumentHealth[] }))}
            />
          </FilterPanel>

          <FilterPanel label="Updated between">
            <div style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 12 }}>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                style={{ flex: 1, padding: 4 }}
              />
              <span>→</span>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
                style={{ flex: 1, padding: 4 }}
              />
            </div>
          </FilterPanel>
        </div>
      )}

      {dragOver && <div className="dropzone dropzone--over" style={{ marginBottom: 16 }}>Drop to upload</div>}

      {isLoading && <SkeletonRows rows={8} />}
      {isError && !searchMode && <EmptyState title="Couldn't load documents" body="Check the API is running." />}
      {/* CTSK-494 Phase 1c — search-mode states. The flag can say "on"
          while the API has since been restarted with it off (route
          unmounted → 404): surface it, offer Retry, never crash. */}
      {searchMode && isError && (
        <EmptyState
          title="Couldn't search documents"
          body={searchQ.error instanceof Error ? searchQ.error.message : 'Check the API is running and document search is enabled.'}
          action={<button className="btn" onClick={() => { void searchQ.refetch(); }}>Retry</button>}
        />
      )}
      {searchMode && searchQ.data && searchResults.length === 0 && !isLoading && (
        <EmptyState
          title={`No results for “${searchQuery}”`}
          body={nestSlug ? 'Try a different query, or clear the nest scope to search everywhere.' : 'Try a different query — titles, bodies and tags are searched.'}
          action={
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              {nestSlug && <button className="btn" onClick={() => setNestSlug(null)}>Search all nests</button>}
              <button className="btn btn--ghost" onClick={() => setFilters((f) => ({ ...f, q: '' }))}>Clear search</button>
            </div>
          }
        />
      )}
      {searchMode && grouping.enabled && searchResults.length > 0 && (
        <RelatedFindingsStrip facets={searchQ.data?.meta.facets} onPick={addLabelFilter} />
      )}
      {/* CTSK-521 — the bulk bar/Select sits ABOVE the results in both modes (list: above the table; search: above the hit list). */}
      {/* 2026-06-03 — selection-mode toggle + bulk action bar.
          Off mode: subtle "Select" button on the right; checkboxes
          in the table are disabled (greyed out, not interactive).
          On mode: bar shows the selection counter, Archive, and (if
          the operator has documents:purge) Delete. Bulk actions
          auto-exit selection mode on success. */}
      {((data && filteredRows.length > 0) || (searchMode && searchResults.length > 0)) && (
        selectionMode ? (
          <div
            data-rocker-anchor
            role="region"
            aria-label="Bulk actions"
            style={{
              background: 'color-mix(in srgb, var(--accent-primary) 8%, var(--surface-raised))',
              border: '1px solid var(--accent-primary)',
              borderRadius: 6,
              padding: '8px 12px',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <strong style={{ fontSize: 13 }}>
              {selectedIds.size === 0
                ? 'Select rows below…'
                : `${selectedIds.size} selected`}
            </strong>
            <button
              className="btn btn--ghost"
              onClick={exitSelectionMode}
              title="Exit selection mode + clear selection"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px' }}
            >
              <X size={13} aria-hidden /> Done
            </button>
            {(searchMode || showCards) && (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input
                  type="checkbox"
                  data-testid="search-select-all"
                  aria-label="Select all results on this page"
                  checked={allVisibleSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected;
                  }}
                  onChange={toggleAllVisible}
                />
                All on this page
              </label>
            )}
            <span style={{ flex: 1 }} />
            {grouping.enabled && (
              <button
                className="btn"
                data-testid="bulk-edit-button"
                disabled={bulkEdit.isPending || selectedIds.size === 0}
                onClick={() => setBulkEditOpen(true)}
                title="Set or clear Group / Collection on the selected documents"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Layers size={14} aria-hidden />
                {`Bulk Edit${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`}
              </button>
            )}
            <button
              className="btn"
              disabled={archiveBulk.isPending || selectedIds.size === 0}
              onClick={() => {
                const n = selectedIds.size;
                if (confirm(`Archive ${n} document${n === 1 ? '' : 's'}?\nArchived documents can be restored from the API.`)) {
                  archiveBulk.mutate([...selectedIds]);
                }
              }}
              title="Soft-delete the selected documents — recoverable via API"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Archive size={14} aria-hidden />
              {archiveBulk.isPending ? 'Archiving…' : `Archive${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`}
            </button>
            {canPurge && (
              <button
                className="btn btn--danger"
                disabled={deleteBulk.isPending || selectedIds.size === 0}
                onClick={() => {
                  const n = selectedIds.size;
                  if (confirm(`PERMANENTLY DELETE ${n} document${n === 1 ? '' : 's'}?\n\nThis cannot be undone — the files and metadata are removed from the database. Use Archive instead if you might want them back.`)) {
                    deleteBulk.mutate([...selectedIds]);
                  }
                }}
                title="Permanently remove the selected documents (owner-only)"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Trash2 size={14} aria-hidden />
                {deleteBulk.isPending ? 'Deleting…' : `Delete${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`}
              </button>
            )}
          </div>
        ) : (
          <div data-rocker-anchor style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button
              className="btn btn--ghost"
              onClick={enterSelectionMode}
              title="Enter multi-select mode to Archive or Delete several documents at once"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
            >
              <Archive size={13} aria-hidden /> Select
            </button>
          </div>
        )
      )}

      {searchMode && searchResults.length > 0 && showCards && (
        <DocumentCardWall
          docs={hitDocs}
          mode={viewMode as 'masonry' | 'grid'}
          expanded={expandedCards}
          columns={cardCols}
          previewAll={previewAll}
          scaled={scaledPreview}
          needsDetail
          actions={cardActions}
          selectable={selectionMode}
          selectedIds={selectedIds}
          onSelect={toggleOne}
          highlightId={highlightId}
          highlightEffect={highlightEffect}
          onOpen={(id) => { rememberOpened(id); onNavigate(`/documents/${id}`); }}
          onToggle={toggleCard}
          renderMeta={(d) => new Date(d.updatedAt).toLocaleDateString()}
          renderSnippet={(d) => snippetSegments(hitById.get(d.id)?.snippet).map((seg, i) => (seg.marked ? <mark key={i}>{seg.text}</mark> : <span key={i}>{seg.text}</span>))}
          renderExtras={(d) => {
            const r = hitById.get(d.id);
            if (!r) return null;
            return (
              <>
                {r.nest && <span className="doccard-tag">{r.nest.slug}</span>}
                {visibleCols.some((c) => c.key === 'intakeClass') && <span className="doccard-tag">{String(r.docType)}</span>}
                {visibleCols.some((c) => c.key === 'tags') && r.tags.slice(0, 4).map((t) => <span key={t} className="doccard-tag">{t}</span>)}
              </>
            );
          }}
        />
      )}
      {searchMode && searchResults.length > 0 && !showCards && (
        <ol
          className="doc-library__search-results"
          aria-label="Search results"
          data-testid="documents-search-results"
        >
          {sortedResults.map((r) => (
            <li
              key={r.id}
              className="doc-library__search-hit"
              style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}
            >
              {selectionMode && (
                <input
                  type="checkbox"
                  aria-label={`Select ${r.title}`}
                  checked={selectedIds.has(r.id)}
                  onChange={() => toggleOne(r.id)}
                  style={{ marginTop: 6 }}
                />
              )}
              <a
                href={r.url}
                style={{ flex: 1, minWidth: 0 }}
                className="doc-library__search-link"
                data-testid="documents-search-hit"
                onClick={(e) => {
                  // Plain left-click → SPA navigate; modified clicks keep
                  // the browser's open-in-new-tab behaviour via href.
                  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                  e.preventDefault();
                  onNavigate(r.url);
                }}
              >
                <DocumentHighlightRow
                  result={r}
                  variant="full"
                  onLabelFilter={addLabelFilter}
                  showTags={visibleCols.some((c) => c.key === 'tags')}
                  showUpdated={visibleCols.some((c) => c.key === 'updatedAt')}
                />
              </a>
              {visibleCols.some((c) => c.key === 'intakeClass') && (
                <span className="doccard-tag" data-testid="search-hit-intake" style={{ alignSelf: 'flex-start', marginLeft: 8 }}>{String(r.docType)}</span>
              )}
            </li>
          ))}
        </ol>
      )}
      {data && filteredRows.length === 0 && !isLoading && (
        <EmptyState
          title={activeFilterCount > 0 ? 'No matches' : 'No documents yet'}
          body={activeFilterCount > 0 ? 'Try different filters or reset.' : 'Upload a file or drop one onto this page.'}
          action={activeFilterCount > 0 ? <button className="btn" onClick={() => { setFilters(EMPTY_FILTERS); setQuickChip('all'); setNestSlug(null); setWatchFilter('all'); setRelatedDocId(null); }}>Reset filters</button> : undefined}
        />
      )}
      {data && filteredRows.length > 0 && showCards && (
        <DocumentCardWall
          docs={sorted}
          mode={viewMode as 'masonry' | 'grid'}
          expanded={expandedCards}
          columns={cardCols}
          previewAll={previewAll}
          scaled={scaledPreview}
          onToggle={toggleCard}
          actions={cardActions}
          selectable={selectionMode}
          selectedIds={selectedIds}
          onSelect={toggleOne}
          highlightId={highlightId}
          highlightEffect={highlightEffect}
          onOpen={(id) => { rememberOpened(id); onNavigate(`/documents/${id}`); }}
          renderExtras={(d) => d.tags.map((t) => (
            <TagBadge key={t.id} tag={t} onClick={(tag) => { setFilters((f) => ({ ...f, tags: [tag.id] })); setQuickChip('all'); setPage(1); }} />
          ))}
        />
      )}
      {data && filteredRows.length > 0 && !showCards && (
        <table className="grid">
          <thead>
            <tr>
              {/* 2026-06-03 — master select-all checkbox. Disabled until
                  the operator enters selection mode via the "Select"
                  button above the table. Indeterminate when only some
                  visible rows are selected. Scope is current page only
                  (visibleIds), so the operator never archives off-page
                  docs they can't see. */}
              <th style={{ width: 32, padding: '0 6px' }}>
                <input
                  type="checkbox"
                  checked={selectionMode && allVisibleSelected}
                  disabled={!selectionMode}
                  ref={(el) => {
                    if (el) el.indeterminate = selectionMode && !allVisibleSelected && someVisibleSelected;
                  }}
                  onChange={toggleAllVisible}
                  title={
                    !selectionMode
                      ? 'Click "Select" above to enable multi-select'
                      : allVisibleSelected ? 'Unselect all visible' : 'Select all visible'
                  }
                  aria-label={allVisibleSelected ? 'Unselect all visible documents' : 'Select all visible documents'}
                  style={!selectionMode ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                />
              </th>
              {/* ctsk-061 — header is column-driven so hide/reorder
                  prefs apply. Sortable cols stay sortable; Tags has no
                  natural sort key so its header is non-sortable. */}
              {visibleCols.map((c, idx) => (
                <SortableTh
                  key={c.key}
                  sortKey={c.key}
                  sort={sort}
                  onSortChange={setSort}
                  sortable={c.sortable}
                  style={{
                    width: c.width,
                    whiteSpace: 'nowrap',
                    // Mirror the tight ⋯ end-of-row spacing on the
                    // first column's left edge (round-3 feedback).
                    paddingLeft: idx === 0 ? FIRST_COL_PADDING_LEFT : undefined,
                    // Compact-trio cells share tighter horizontal
                    // padding so the badge cells sit closer together.
                    padding: COMPACT_KEYS.has(c.key) ? COMPACT_CELL_PADDING : undefined,
                  }}
                >
                  {c.label}
                </SortableTh>
              ))}
              {/* Actions column — tight slot for the ⋯ menu trigger.
                  Width 28 + zero side-padding keeps the row's right
                  edge close to the last data cell instead of leaving
                  a big gap. */}
              <th style={{ width: 28, padding: 0 }}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((d) => (
              <DocumentRow
                key={d.id}
                doc={d}
                cols={visibleCols}
                selectionMode={selectionMode}
                selected={selectedIds.has(d.id)}
                onToggleSelect={() => toggleOne(d.id)}
                onNavigate={onNavigate}
                onFilterContainer={(id) => setFilters((f) => ({ ...f, containerId: id }))}
                onTagClick={(tagId) => {
                  // Clicking a tag chip narrows the page to just
                  // documents carrying that single tag. Resets the
                  // quick chip + page so the result set is
                  // unambiguous (no leftover "Untagged" filter
                  // fighting the new tag filter).
                  setFilters((f) => ({ ...f, tags: [tagId] }));
                  setQuickChip('all');
                  setPage(1);
                }}
                onMoreLikeThis={() => {
                  // ctsk-050 — "More like this": clone this doc's tag
                  // set into the active filter. Land back on page 1 so
                  // we see the freshest matches first.
                  setFilters((f) => ({ ...f, tags: d.tags.map((t) => t.id) }));
                  setQuickChip('all');
                  setPage(1);
                  toast.success(`Filtered by ${d.tags.length} tag${d.tags.length === 1 ? '' : 's'} from "${d.title}"`);
                }}
                onAction={() => qc.invalidateQueries({ queryKey: ['documents'] })}
              />
            ))}
          </tbody>
        </table>
      )}

      {/* Full pagination control at the bottom for the spec's per-page
          selector with the count summary visible (ctsk-054). Hidden when
          there's only one page — top toolbar still has the compact form
          if the user wants to change page-size from a small list. */}
      {((data && filteredRows.length > 0) || searchResults.length > 0) && totalPages > 1 && (
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <Pagination
            page={page}
            totalPages={totalPages}
            pageSize={effectivePageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(n) => setPageSize(n as PageSize)}
            pageSizes={pageSizeOptions}
          />
        </div>
      )}

      <BulkEditLabelsModal
        open={bulkEditOpen}
        count={selectedIds.size}
        pageScope
        showConfirmation={grouping.showBulkConfirmationDialogs}
        isPending={bulkEdit.isPending}
        onApply={(m) => bulkEdit.mutate(m)}
        onCancel={() => setBulkEditOpen(false)}
      />
      {folderDrop && (
        <FolderDropDialog
          summary={folderDrop}
          groupingEnabled={grouping.enabled}
          autoNameMode={grouping.groupingAutoNameFromFolder}
          maxFiles={grouping.groupingMaxFilesPerDrop}
          onCancel={() => setFolderDrop(null)}
          onConfirm={({ groupName }) => {
            const summary = folderDrop;
            setFolderDrop(null);
            upload.enqueueDirectory(summary.included, {
              groupName,
              report: {
                unsupported: summary.counts.unsupported,
                unsupportedNames: summary.unsupportedNames,
                overCap: summary.counts.overCap,
              },
            });
          }}
        />
      )}
      <BulkUploadPanel
        queue={upload.queue}
        activeCount={upload.activeCount}
        queuedCount={upload.queuedCount}
        doneCount={upload.doneCount}
        errorCount={upload.errorCount}
        onDismiss={upload.dismiss}
      />
    </div>
  );
}

// ─── DocumentRow ──────────────────────────────────────────────────────
//
// ctsk-050 — the spec's 7 row actions, restored:
//   1. Open in new tab          (window.open detail page)
//   2. View                     (navigate within current tab)
//   3. Download                 (file content endpoint with ?download)
//   4. More like this           (filter docs page by this doc's tags)
//   5. Edit metadata            (navigate to detail page, Metadata tab)
//   6. Add tags                 (navigate to detail page, Tags section)
//   7. Delete                   (DELETE + confirm)
// ctsk-052 — bonus: Share link (copy permalink). Slots after "Download".
//
// Note: Edit metadata + Add tags both navigate to the detail page; the
// detail page already has those affordances visible without further
// navigation. If the user wants deep-linking to a specific tab they can
// follow up — the spec doesn't require deep-linking, just access.

function DocumentRow({
  doc,
  cols,
  selectionMode,
  selected,
  onToggleSelect,
  onNavigate,
  onFilterContainer,
  onTagClick,
  onMoreLikeThis,
  onAction,
}: {
  doc: DocumentListItem;
  /** ctsk-061 — column order/visibility, sourced from useColPrefs. */
  cols: ColumnDef[];
  /** 2026-06-03 — multi-select mode flag. When false the checkbox is
   *  rendered but disabled (visually muted, not interactive). */
  selectionMode: boolean;
  /** 2026-06-03 — bulk select state for this row. */
  selected: boolean;
  /** Toggle the row's bulk-select state. Click on the checkbox itself
   *  stops propagation so it doesn't open the document. */
  onToggleSelect: () => void;
  onNavigate: (p: string) => void;
  onFilterContainer: (id: string) => void;
  /** Clicking a tag chip in the Tags column filters the page to just
   *  documents carrying that tag. The handler is owned by the parent
   *  so it can also reset pagination + clear the quick-filter chip. */
  onTagClick: (tagId: string) => void;
  onMoreLikeThis: () => void;
  onAction: () => void;
}) {
  const del = useMutation({
    mutationFn: () => apiFetch<void>(`/documents/${doc.id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); onAction(); },
    onError: (e: Error) => toast.error(e.message),
  });

  // ctsk-051 — Watch/unwatch, shared by the row ellipsis menu and the
  // CTSK-246 star column. Idempotent on the server.
  //
  // CTSK-246 — upgraded from invalidate-on-success to the CTSK-237
  // optimistic pattern: the star flips instantly via a cache patch on
  // every ["documents"] list query (the Watching page's docs query
  // shares the prefix, so it stays in sync too), rolls back from the
  // onMutate snapshot on server error, and re-syncs with the server on
  // settle either way. Without this the star column would flicker
  // between click and refetch. The old onSuccess → onAction() is
  // subsumed by onSettled's invalidate (same query key).
  const qc = useQueryClient();
  const watch = useMutation({
    mutationFn: (next: boolean) =>
      apiSingle<{ documentId: string; isWatchedByMe: boolean; watcherCount: number }>(
        `/documents/${doc.id}/watch`,
        { method: next ? 'POST' : 'DELETE' },
      ),
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: ['documents'] });
      const prev = qc.getQueriesData<{ data: DocumentListItem[] }>({
        queryKey: ['documents'],
      });
      qc.setQueriesData<{ data: DocumentListItem[] }>(
        { queryKey: ['documents'] },
        (page) =>
          page
            ? {
                ...page,
                data: page.data.map((row) =>
                  row.id === doc.id ? { ...row, isWatchedByMe: next } : row,
                ),
              }
            : page,
      );
      return { prev };
    },
    onError: (e: Error, _next, ctx) => {
      for (const [key, data] of ctx?.prev ?? []) qc.setQueryData(key, data);
      toast.error(e.message);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });

  // ctsk-061 — column-driven cell renderer. The Container cell needs
  // to swallow row-click (its child buttons are filter actions), so it
  // returns a tuple of { content, onClick } that the wrapping <td>
  // honors. Everything else is a plain ReactNode.
  function renderCell(c: ColumnDef): React.ReactNode {
    switch (c.key) {
      case 'watch': {
        // CTSK-246 — star toggle, mirroring WorkItemsList's CTSK-237
        // cell. ★ filled = watching (accent), ☆ empty = not (muted).
        // Click toggles in place; stopPropagation so the row's
        // navigate-on-click doesn't fire. Tooltip copy matches
        // CTSK-237 exactly (AC#9); aria-labels carry the "document"
        // noun per AC#8. Native <button> gives Space/Enter keyboard
        // toggling for free.
        const watching = doc.isWatchedByMe ?? false;
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              watch.mutate(!watching);
            }}
            title={watching ? 'Unwatch' : 'Watch'}
            aria-label={watching ? 'Unwatch document' : 'Watch document'}
            aria-pressed={watching}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 2,
              fontSize: 16,
              color: watching
                ? 'var(--accent-primary)'
                : 'var(--text-muted)',
              lineHeight: 1,
            }}
          >
            {watching ? '★' : '☆'}
          </button>
        );
      }
      case 'title':
        // Lucide MimeIcon (replaces the emoji glyph) + a slightly
        // larger / medium-weight title so the column stands out
        // without going back to the loud <strong> from before
        // ctsk-060. Title truncates with ellipsis on overflow; the
        // <td>'s title="" attr surfaces the full string on hover.
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span
              title={iconLabelForMime(doc.mimeType)}
              aria-label={iconLabelForMime(doc.mimeType)}
              style={{ display: 'inline-flex', color: 'var(--text-muted)' }}
            >
              <MimeIcon mime={doc.mimeType} size={16} />
            </span>
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                fontSize: 14,
                fontWeight: 500,
                color: 'var(--text-primary)',
              }}
            >
              {doc.title}
            </span>
          </span>
        );
      case 'openInBrowser':
        // HTML data type — quick "Open in browser" affordance. Empty
        // cell for non-HTML rows so the column is visually quiet
        // (matches StorageContainerDetail's identical column).
        if (!doc.mimeType.startsWith('text/html')) return null;
        return (
          <a
            href={`/api/v1/documents/${doc.id}/content`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={`Open "${doc.title}" in a new tab`}
            aria-label={`Open "${doc.title}" in a new tab`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              color: 'var(--text-muted)',
              padding: 2,
            }}
          >
            <ExternalLink size={14} aria-hidden />
          </a>
        );
      case 'intakeClass':
        // Plain small text, matching the Triggers column on
        // TaggingRulesAdmin (`style={{ fontSize: 12 }}`). The earlier
        // colored-pill treatment was too loud for a category that's
        // mostly a metadata footnote.
        return <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{doc.intakeClass}</span>;
      case 'tags':
        // Clickable chips — filter the page to just docs carrying this
        // tag. We replace (not merge) so a single click is a clear
        // "show me docs with tag X" gesture; user can layer more from
        // the advanced filters if they want a multi-tag query.
        return doc.tags.map((t) => (
          <TagBadge
            key={t.id}
            tag={t}
            onClick={(tag) => onTagClick(tag.id)}
          />
        ));
      case 'container':
        return doc.containers.length === 0
          ? <span style={{ color: 'var(--text-muted)' }}>—</span>
          : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {doc.containers.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onFilterContainer(c.id); }}
                  title={`Filter by container: ${c.name}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '2px 8px',
                    borderRadius: 999,
                    fontSize: 12,
                    border: '1px solid var(--border-strong)',
                    background: 'var(--surface-raised)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: c.color ?? 'var(--accent-primary)' }} />
                  {c.name}
                </button>
              ))}
            </div>
          );
      case 'status':
        return <StatusBadge status={doc.status} variant="document" />;
      case 'health':
        return <StatusBadge status={doc.health} variant="health" />;
      case 'sizeBytes':
        return formatBytes(doc.sizeBytes);
      case 'updatedAt':
        return new Date(doc.updatedAt).toLocaleString();
      case 'createdAt':
        return new Date(doc.createdAt).toLocaleDateString();
      case 'groupName':
        return doc.groupName ?? '';
      case 'collectionName':
        return doc.collectionName ?? '';
      case 'sourceFolder':
        return doc.sourceFolder ?? '';
      default:
        return null;
    }
  }

  return (
    <tr
      style={{
        cursor: 'pointer',
        background: selected ? 'color-mix(in srgb, var(--accent-primary) 6%, transparent)' : undefined,
      }}
      onClick={() => onNavigate(`/documents/${doc.id}`)}
    >
      {/* 2026-06-03 — bulk select checkbox cell. Click swallowed so
          ticking the box doesn't open the document. Disabled (visibly
          muted) until selection mode is active. */}
      <td
        style={{ width: 32, padding: '0 6px' }}
        onClick={(e) => { e.stopPropagation(); }}
      >
        <input
          type="checkbox"
          checked={selectionMode && selected}
          disabled={!selectionMode}
          onChange={onToggleSelect}
          aria-label={`Select ${doc.title}`}
          title={selectionMode ? `Select ${doc.title}` : 'Click "Select" above to enable multi-select'}
          style={!selectionMode ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
        />
      </td>
      {cols.map((c, idx) => {
        // Cells with a width hint also get whiteSpace:nowrap so dates,
        // sizes, and status badges don't wrap into two lines when the
        // viewport tightens. The Title cell honors its width as a max
        // and clips with ellipsis (rendered inside renderCell), and
        // the Tags cell stays free to flex-wrap chips inside.
        const noWrap = c.key !== 'tags' && c.key !== 'container';
        // Title cell also gets a hover-full-text title attr so the
        // ellipsis doesn't hide long names.
        const titleAttr = c.key === 'title' ? doc.title : undefined;
        // Compact-trio cells (container/status/health/size) and the
        // first column's left edge get tighter padding than the
        // .grid default. See FIRST_COL_PADDING_LEFT + COMPACT_KEYS
        // above for the why.
        const isCompact = COMPACT_KEYS.has(c.key);
        return (
          <td
            key={c.key}
            title={titleAttr}
            style={{
              width: c.width,
              maxWidth: c.width,
              whiteSpace: noWrap ? 'nowrap' : undefined,
              overflow: noWrap ? 'hidden' : undefined,
              textOverflow: noWrap ? 'ellipsis' : undefined,
              paddingLeft: idx === 0 ? FIRST_COL_PADDING_LEFT : undefined,
              padding: isCompact ? COMPACT_CELL_PADDING : undefined,
            }}
          >
            {renderCell(c)}
          </td>
        );
      })}
      <td
        onClick={(e) => e.stopPropagation()}
        style={{ width: 56, padding: 0, textAlign: 'center', whiteSpace: 'nowrap' }}
      >
        {/* CTSK-354 — copy the /dc/:id short URL for this document. */}
        <CopyShortUrlButton shortPath={`/dc/${doc.id}`} label={doc.title} />
        <RowActionsMenu
          actions={[
            { label: 'Open in new tab', onClick: () => window.open(`/documents/${doc.id}`, '_blank') },
            { label: 'View', onClick: () => onNavigate(`/documents/${doc.id}`) },
            { label: 'Download', onClick: () => {
              // Use a hidden <a download> click rather than window.open
              // so the browser respects the download attribute.
              const a = document.createElement('a');
              a.href = `/api/v1/documents/${doc.id}/content`;
              a.setAttribute('download', doc.originalFilename ?? doc.title);
              document.body.appendChild(a);
              a.click();
              a.remove();
            } },
            { label: '', onClick: () => undefined, separator: true },
            // ctsk-051 — Watch / Unwatch toggle. Label flips based on
            // current state so a single menu entry covers both
            // directions.
            {
              label: doc.isWatchedByMe ? '☆ Unwatch' : '★ Watch',
              onClick: () => watch.mutate(!doc.isWatchedByMe),
            },
            { label: 'More like this', onClick: onMoreLikeThis },
            { label: 'Share link', onClick: async () => {
              // ctsk-052 — copy permalink to clipboard. No ACL change.
              const url = `${window.location.origin}/documents/${doc.id}`;
              const ok = await copyToClipboard(url);
              if (ok) toast.success('Link copied');
              else toast.error('Could not copy — your browser blocked clipboard access');
            } },
            { label: '', onClick: () => undefined, separator: true },
            { label: 'Edit metadata', onClick: () => onNavigate(`/documents/${doc.id}`) },
            { label: 'Add tags', onClick: () => onNavigate(`/documents/${doc.id}`) },
            { label: '', onClick: () => undefined, separator: true },
            { label: 'Delete', destructive: true, onClick: () => {
              if (confirm(`Delete "${doc.title}"?\nThe file is moved to a soft-deleted state and can be restored from the API.`)) {
                del.mutate();
              }
            } },
          ]}
        />
      </td>
    </tr>
  );
}

// ─── Quick-filter chip row (ctsk-053) ─────────────────────────────────

function QuickFilterChips({
  value,
  onChange,
  recentDays,
}: {
  value: QuickChip;
  onChange: (v: QuickChip) => void;
  recentDays: number;
}) {
  const chips: { value: QuickChip; label: string; glyph: string; title?: string; disabled?: boolean }[] = [
    { value: 'untagged', label: 'Untagged', glyph: '○', title: 'Documents with no tags' },
    { value: 'nest',     label: 'In a nest', glyph: '◆', title: 'Documents carrying any nest/<slug> master tag' },
    { value: 'recent',   label: `Recent (${recentDays}d)`, glyph: '✦', title: `Documents created in the last ${recentDays} days` },
    { value: 'review',   label: 'Needs review', glyph: '!', title: 'Status pending/draft or health warning' },
    { value: 'starred',  label: 'Starred tags', glyph: '★', title: 'Tag starring ships in a follow-up — see FEATURE_REQUESTS.md', disabled: true },
  ];

  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        marginBottom: 8,
        fontSize: 12,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ color: 'var(--text-muted)' }}>Quick filter:</span>
      <button
        type="button"
        onClick={() => onChange('all')}
        style={chipStyle(value === 'all', false)}
        aria-pressed={value === 'all'}
      >
        ∗ All
      </button>
      {chips.map((c) => {
        const active = value === c.value;
        return (
          <button
            key={c.value}
            type="button"
            onClick={() => { if (!c.disabled) onChange(active ? 'all' : c.value); }}
            title={c.title}
            disabled={c.disabled}
            aria-pressed={active}
            style={chipStyle(active, !!c.disabled)}
          >
            <span style={{ marginRight: 4, opacity: 0.75 }}>{c.glyph}</span>
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

// ctsk-051 — Watching segmented control. Three states (All / Watching /
// Not watching) — matches the work-items pattern from #112. Server-side
// via ?watchedBy so the page result set reflects the filter (not just
// a client-side narrow), which keeps pagination math accurate.
function WatchSegmented({
  value,
  onChange,
}: {
  value: 'all' | 'mine' | 'not-mine';
  onChange: (v: 'all' | 'mine' | 'not-mine') => void;
}) {
  const options: Array<{ value: 'all' | 'mine' | 'not-mine'; label: string; glyph: string }> = [
    { value: 'all',      label: 'All',          glyph: '∗' },
    { value: 'mine',     label: 'Watching',     glyph: '★' },
    { value: 'not-mine', label: 'Not',          glyph: '☆' },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="watching"
      style={{
        display: 'inline-flex',
        border: '1px solid var(--border-strong)',
        borderRadius: 6,
        overflow: 'hidden',
        fontSize: 12,
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            title={
              o.value === 'all'
                ? 'Show every document'
                : o.value === 'mine'
                  ? 'Show only documents you are watching'
                  : 'Hide documents you are watching'
            }
            style={{
              padding: '4px 10px',
              fontSize: 12,
              border: 'none',
              background: active ? 'var(--accent-primary)' : 'var(--surface-raised)',
              color: active ? '#fff' : 'var(--text-primary)',
              cursor: 'pointer',
              fontWeight: active ? 600 : 400,
            }}
          >
            <span style={{ marginRight: 4, opacity: 0.75 }}>{o.glyph}</span>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function chipStyle(active: boolean, disabled: boolean): React.CSSProperties {
  return {
    padding: '4px 10px',
    fontSize: 12,
    borderRadius: 6,
    border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-strong)'}`,
    background: active
      ? 'color-mix(in srgb, var(--accent-primary) 12%, var(--surface-raised))'
      : 'var(--surface-raised)',
    color: disabled
      ? 'var(--text-muted)'
      : (active ? 'var(--accent-primary-hover)' : 'var(--text-primary)'),
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: active ? 600 : 400,
    opacity: disabled ? 0.55 : 1,
  };
}

function FilterPanel({
  label,
  hint,
  children,
}: {
  label: string;
  /** Optional small caption rendered on the same row as the label —
   *  used by the Tags panel to surface "5 of 32 in view · 27 hidden". */
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 6,
        }}
      >
        <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
          {label}
        </span>
        {hint && (
          <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0 }}>
            {hint}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function MultiChip({
  options,
  selected,
  onChange,
  empty,
}: {
  options: { value: string; label: string; dot?: string | null }[];
  selected: string[];
  onChange: (next: string[]) => void;
  empty?: string;
}) {
  if (options.length === 0 && empty) {
    return <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{empty}</span>;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {options.map((o) => {
        const active = selected.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(active ? selected.filter((x) => x !== o.value) : [...selected, o.value])}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '2px 8px',
              borderRadius: 999,
              fontSize: 12,
              border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-strong)'}`,
              background: active ? 'color-mix(in srgb, var(--accent-primary) 12%, var(--surface-raised))' : 'var(--surface-raised)',
              color: active ? 'var(--accent-primary-hover)' : 'var(--text-primary)',
              cursor: 'pointer',
            }}
          >
            {o.dot && <span style={{ width: 8, height: 8, borderRadius: '50%', background: o.dot, display: 'inline-block' }} />}
            {o.label}
          </button>
        );

      })}
    </div>
  );
}
